// src/pages/auth/Login.tsx
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { apiPost } from '../../services/api';
import { getEnvironmentConfig } from '../../services/environment';
import { useAuth } from '../../contexts/AuthContext';
import { showAlert } from '../../contexts/DialogContext';
import { resolveAuthError } from '../../services/authError';
import { useConfiguracaoLogin } from '../../hooks/useConfiguracaoLogin';
import AnimatedCanvas from '../../components/common/AnimatedCanvas';
import '../../pages/auth/Login.css';
import logoBluelt from '../../assets/images/logoBluelt.png';
import logoMicrosoft from '../../assets/images/logo_microsoft_sf.png';
import {
  buildMicrosoftAuthorizeUrl,
  decodeState,
  isAllowedTenantOrigin,
  MICROSOFT_CALLBACK_ORIGIN,
} from '../../services/msalConfig';
import { logger } from '@/utils/logger';

// Espera padrão quando o 429 não traz o tempo (segundos).
const RATE_LIMIT_FALLBACK_SECONDS = 60;

// Extrai o tempo de espera do erro 429 (corpo `retryAfterSeconds` ou header
// `Retry-After`); cai no fallback quando ausente.
function extractRetryAfterSeconds(error: unknown): number {
  const resp = (error as
    | { response?: { data?: { retryAfterSeconds?: number }; headers?: Record<string, string> } }
    | undefined)?.response;
  const fromBody = resp?.data?.retryAfterSeconds;
  if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody > 0) {
    return Math.ceil(fromBody);
  }
  const header = resp?.headers?.['retry-after'];
  const fromHeader = header ? parseInt(header, 10) : NaN;
  if (Number.isFinite(fromHeader) && fromHeader > 0) return fromHeader;
  return RATE_LIMIT_FALLBACK_SECONDS;
}

// Formata segundos como m:ss para a contagem regressiva.
function formatCooldown(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const Login = () => {
  const { BASE_URL_BACKEND, API_CONTEXT } = getEnvironmentConfig();
  const backendOrigin = BASE_URL_BACKEND.replace(/\/(dev|test|prod)$/, '');
  const microsoftLoginEndpoint = `${backendOrigin}/${API_CONTEXT}/v1/auth/microsoft-login`;
  const navigate = useNavigate();
  const { login } = useAuth(); // usa função login do AuthContext
  const [userName, setUserName] = useState('');
  const [senha, setSenha] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const popupMonitorRef = useRef<number | null>(null);
  const isAnyLoading = isLoading || isMicrosoftLoading;

  // Config de login do tenant (issue #28): decide quais métodos renderizar.
  const { config, isLoading: isConfigLoading } = useConfiguracaoLogin();
  // Contagem regressiva do rate-limit (429).
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const isCoolingDown = cooldownSeconds > 0;

  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const id = window.setTimeout(() => setCooldownSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [cooldownSeconds]);

  useEffect(() => {
    document.body.classList.add('login-page');
    return () => {
      document.body.classList.remove('login-page');
    };
  }, []);

  useEffect(() => {
    document.title = 'Bluelt | Login';
  }, []);

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const idToken = hashParams.get('id_token');
    const stateParam = hashParams.get('state');
    const oauthError = hashParams.get('error');
    const oauthErrorDescription = hashParams.get('error_description');

    if (!window.opener || (!idToken && !oauthError)) {
      return;
    }

    if (!stateParam) {
      return;
    }

    const decodedState = decodeState(stateParam);
    if (!decodedState || !isAllowedTenantOrigin(decodedState.tenantOrigin)) {
      return;
    }

    window.opener.postMessage(
      {
        type: 'MICROSOFT_AUTH_RESULT',
        ok: !oauthError,
        idToken,
        error: oauthError ? oauthErrorDescription || oauthError : undefined,
        timestamp: Date.now(),
      },
      decodedState.tenantOrigin
    );

    window.setTimeout(() => {
      window.close();
    }, 150);
  }, []);

  useEffect(() => {
    const handleMicrosoftCallbackMessage = async (event: MessageEvent) => {
      if (event.origin !== MICROSOFT_CALLBACK_ORIGIN) {
        return;
      }

      const data = event.data as {
        type?: string;
        ok?: boolean;
        idToken?: string;
        error?: string;
      };

      if (!data || data.type !== 'MICROSOFT_AUTH_RESULT') {
        return;
      }

      if (popupMonitorRef.current) {
        window.clearInterval(popupMonitorRef.current);
        popupMonitorRef.current = null;
      }

      if (!data.ok) {
        setIsMicrosoftLoading(false);
        await showAlert(data.error || 'Falha na autenticação Microsoft.', { variant: 'error' });
        return;
      }

      if (!data.idToken) {
        setIsMicrosoftLoading(false);
        await showAlert('ID Token Microsoft não recebido.', { variant: 'error' });
        return;
      }

      try {
        const response = await apiPost(microsoftLoginEndpoint, { idToken: data.idToken }, { blocking: true });
        const token = response.data.Authorization?.replace('Bearer ', '');
        if (token) {
          login(token);
          navigate('/home');
        } else {
          await showAlert('Token interno não encontrado na resposta do backend.', { variant: 'error' });
        }
      } catch (error) {
        logger.error('Erro ao logar com Microsoft:', error);
        if (!applyRateLimitCooldown(error)) {
          const { message, variant } = resolveAuthError(error, 'Falha ao autenticar com Microsoft.');
          await showAlert(message, { variant });
        }
      } finally {
        setIsMicrosoftLoading(false);
        popupRef.current?.close();
        popupRef.current = null;
      }
    };

    window.addEventListener('message', handleMicrosoftCallbackMessage);
    return () => {
      window.removeEventListener('message', handleMicrosoftCallbackMessage);
      if (popupMonitorRef.current) {
        window.clearInterval(popupMonitorRef.current);
      }
    };
  }, [login, microsoftLoginEndpoint, navigate]);

  // Detecta rate-limit (429) via resolveAuthError e inicia a contagem regressiva
  // em vez de um alerta genérico. Retorna true quando tratou como rate-limit.
  const applyRateLimitCooldown = (error: unknown): boolean => {
    const { variant } = resolveAuthError(error, '');
    if (variant === 'warning') {
      setCooldownSeconds(extractRetryAfterSeconds(error));
      return true;
    }
    return false;
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isMicrosoftLoading) {
      return;
    }
    if (!e.currentTarget.checkValidity()) {
      e.currentTarget.reportValidity();
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiPost('/login', { userName, senha });
      const token = response.data.Authorization?.replace('Bearer ', '');
      if (token) {
        login(token); // chama função login do AuthContext
        navigate('/home');
      } else {
        await showAlert('Token não encontrado na resposta!', { variant: 'error' });
      }
    } catch (error) {
      logger.error('Erro ao logar:', error);
      if (!applyRateLimitCooldown(error)) {
        const { message, variant } = resolveAuthError(error, 'Servidor indisponível no momento, tente novamente mais tarde.');
        await showAlert(message, { variant });
      }
    } finally {
      setIsLoading(false);
    }
  };

  function formatarUserName(valor: string): string {
    let v = valor.trim();
    const digitsOnly = v.replace(/\D/g, '');
    if (digitsOnly.length >= 7) {
      v = digitsOnly.slice(0, 11);
    } else {
      v = v.toLowerCase();
    }
    return v;
  }

  const handleMicrosoftLogin = async () => {
    setIsMicrosoftLoading(true);
    const authUrl = buildMicrosoftAuthorizeUrl(window.location.origin);
    popupRef.current = window.open(
      authUrl,
      'microsoftAuthPopup',
      'width=520,height=680,menubar=no,toolbar=no,location=no,resizable=yes,scrollbars=yes,status=no'
    );

    if (!popupRef.current) {
      setIsMicrosoftLoading(false);
      await showAlert('O popup foi bloqueado pelo navegador. Permita popups e tente novamente.', { variant: 'warning' });
      return;
    }

    popupMonitorRef.current = window.setInterval(() => {
      if (popupRef.current?.closed) {
        if (popupMonitorRef.current) {
          window.clearInterval(popupMonitorRef.current);
          popupMonitorRef.current = null;
        }
        popupRef.current = null;
        setIsMicrosoftLoading(false);
      }
    }, 500);
  };

  return (
    <>
      <AnimatedCanvas />
      <div className="auth-container">
        <form id="login-form" onSubmit={handleLogin} noValidate>
          <img src={logoBluelt} alt="Logo Bluelt" className="logo" />
          <h3>Login</h3>
          {isConfigLoading || !config ? (
            <p className="login-config-loading">Carregando opções de acesso…</p>
          ) : (
            <>
              {/* Renderização condicional por tenant (issue #28). Fallback do hook
                  já mostra os dois quando o GET falha. */}
              {config.permiteSenha && (
                <>
                  <div className="form-group">
                    <input
                      type="text"
                      id="login-email"
                      placeholder="Digite username, email ou cpf"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      onBlur={(e) => setUserName(formatarUserName(e.target.value))}
                      required
                      disabled={isAnyLoading || isCoolingDown}
                    />
                  </div>
                  <div className="form-group">
                    <input
                      type="password"
                      id="login-password"
                      placeholder="Digite sua senha"
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      required
                      disabled={isAnyLoading || isCoolingDown}
                    />
                  </div>
                  {!isLoading ? (
                    <button type="submit" className="btn-submit" disabled={isMicrosoftLoading || isCoolingDown}>Entrar</button>
                  ) : (
                    <button type="button" className="btn-clone" disabled>
                      <div className="spinner"></div>
                    </button>
                  )}
                </>
              )}

              {config.permiteMicrosoft && (
                <button
                  type="button"
                  className="btn-microsoft"
                  onClick={handleMicrosoftLogin}
                  disabled={isAnyLoading || isCoolingDown}
                >
                  <span className="btn-microsoft-content">
                    <img src={logoMicrosoft} alt="" className="btn-microsoft-logo" aria-hidden="true" />
                    <span>{isMicrosoftLoading ? 'Conectando com Microsoft...' : 'Entrar com Microsoft'}</span>
                  </span>
                </button>
              )}

              {isCoolingDown && (
                <p className="login-cooldown" role="alert">
                  Muitas tentativas. Tente novamente em {formatCooldown(cooldownSeconds)}.
                </p>
              )}

              <div className="area-suporte">
                <a
                  className="forgot-link"
                  href="https://bluelt.atlassian.net/servicedesk/customer/portal/1/user/login?destination=portal%2F1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Precisa de ajuda?
                </a>
                {config.permiteSenha && (
                  <a className="forgot-link" href="/password-reset">Esqueci minha senha</a>
                )}
              </div>
            </>
          )}
        </form>
      </div>
    </>
  );
};

export default Login;
